import React, { useState, useEffect, useRef } from "react";
import {
  ExternalLinkIcon,
  FilterIcon,
  ChevronDownIcon,
} from "@heroicons/react/outline";

import Link from "next/link";
import { motion } from "framer-motion";
import PropTypes from "prop-types";
import classNames from "classnames";
import * as Text from "@/components/text";
import { createCalendarEvent } from "@/utils/createEvent";
import Button from "@/components/actions/Button";
import { format } from "date-fns";
import breakpoints from "@/constants/breakpoinst";
import useWindowSize from "@/hooks/useWindowSize";
import axios from "axios";
import Metadata from "./Metadata";

import {
  ArrowNarrowRightIcon,
  CalendarIcon,
  FilmIcon,
  ShareIcon,
  VideoCameraIcon,
} from "@heroicons/react/outline";
import { TextLinkMd } from "../text/Link.stories";

const pVariant = {
  initial: {},
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
  exit: {},
};
const cVariant = {
  initial: { opacity: 0, scale: 0, rotateZ: -90 },
  animate: { opacity: 1, scale: 1, rotateZ: 0 },
  exit: {},
};

const monthNames = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

const eventOptions = {
  download: (
    hour,
    day,
    month,
    year,
    diffMinutes,
    title,
    location,
    url,
    type
  ) => {
    createCalendarEvent(
      {
        hour,
        day,
        month,
        year,
        diffMinutes,
        title,
        location,
        url,
      },
      type
    );
  },
  buy: (url, venue) => {
    window.location.href = url + venue;
  },
  locate: (url, venue) => {
    window.location.href = url + venue;
  },
  share: () => {
    navigator.clipboard.writeText(window.location.href);
  },
  navigate: (url, venue) => {
    window.location.href = url + venue;
  },
};

export default function Projections({
  film_projections = {
    from_date: "",
    to_date: "",
    date: "",
    time: "20:00",
    time_zone: "CST",
    venue: "Nombre de la sede",
    billboard_link: "www.google.com/venue_billboard",
    online_purchase_link: "www.google.com/buy_venue_ticket",
    type: "open_tv",
    cost: false,
  },
  image = "",
  movie = {},
  id = "",
  fullDate = true,
  imageDataLink = "",
  trailer = "",
  showTitle = false,
  showImage = false,
  type = film_projections.type,
  label = "",
  displayDate = null,
  view_more = "",
  movie_title = film_projections.movie_title,
  movie_link = "",
  theme = "light",
  show_location = false,
  className,
  maps_url = film_projections.maps_url || "",
  setChild,
}) {
  const [open, setOpen] = useState(false);
  const windowSize = useWindowSize();
  const [visible, toggleVisible] = useState(false);

  const fromDate = film_projections.from_date
    ? new Date(film_projections.from_date)
    : null;

  const toDate = film_projections.to_date
    ? new Date(film_projections.to_date)
    : new Date(film_projections.date);
  const startHour = "10:10:10";
  const bRef = useRef(null);
  const venueUrl = maps_url
    ? maps_url
    : film_projections.venue && film_projections.city
    ? "https://www.google.com/maps/search/?api=1&query=" +
      encodeURIComponent(film_projections.venue + " " + film_projections.city)
    : null;

  const diffMinutes = (fromDate, toDate) => {
    const diff = toDate - fromDate;
    return Math.floor(diff / 1000 / 60);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7 }}
      whileInView={{ opacity: 1, scale: 1 }}
      onMouseLeave={() => toggleVisible(false)}
      onMouseEnter={() => toggleVisible(true)}
      className={classNames(
        showImage ? "" : "border-neutral-900 ",
        "group relative min-w-[200px] overflow-hidden    "
      )}
    >
      <div
        className={classNames(
          " left-0 top-0 z-40 flex h-full items-center gap-3 bg-transparent align-middle lg:gap-4   "
        )}
      >
        <div className=" aspect-[2/3] max-h-[216px] min-h-[152px] min-w-[96px]	max-w-[96px] self-baseline bg-gray-100 lg:h-full lg:min-w-[124px] lg:max-w-[124px]  ">
          <img className="h-full w-full   object-cover " src={image}></img>
        </div>

        <div className="ml-3 space-y-3 lg:ml-4">
          {fromDate && (
            <div>
              <Text.Body
                className="font-bold"
                size={windowSize.width > breakpoints.lg ? "xl" : "lg"}
              >
                {fullDate && fromDate.getDate()}&nbsp;
                {fullDate && monthNames[fromDate.getMonth()]}&nbsp;
                {/* {fullDate && fromDate.getDay()}&nbsp; */}
                {/* {fullDate && fromDate.getFullYear()}&nbsp; */}
                {displayDate || format(fromDate, "p")}
                {/* {type === "onLine" && "-"}&nbsp;
                {type === "onLine" && toDate.getDate()}&nbsp;
                {type === "onLine" && monthNames[toDate.getMonth()]}&nbsp;
                {type === "onLine" && toDate.getFullYear()} */}
              </Text.Body>
            </div>
          )}

          {label ? (
            <Text.Body size="md" className="bg-neutral-100 px-2 py-1">
              {label}
            </Text.Body>
          ) : null}

          <div className="space-y-2">
            <Text.Title
              className="line-clamp-3 max-w-[200px] normal-case"
              as="p"
              // className="font-bold"
              size={windowSize.width > breakpoints.lg ? "md" : "sm"}
            >
              {movie_title}
            </Text.Title>
            {film_projections?.venue ? (
              <div className="py-3">
                <Text.ProperNoun as="p" size="md">
                  {film_projections?.venue}
                </Text.ProperNoun>
                <Text.ProperNoun as="p" size="sm">
                  {film_projections.city}
                </Text.ProperNoun>
              </div>
            ) : null}
            {movie?.attributes?.Directores?.length[0] ? (
              <Text.Body size="md">
                {movie?.attributes.Directores.map((director, i) => (
                  <span className="pr-2" key={i}>
                    {director}&nbsp;
                    {i === movie?.attributes.Directores.length - 1 ? null : ","}
                  </span>
                ))}
              </Text.Body>
            ) : null}

            {/* {movie?.post_meta._amb_duracion[0] ? (
              <Metadata
                size="sm"
                film_metadata={{
                  year: movie?.post_meta._amb_anio,
                  duration: movie?.post_meta._amb_duracion,
                  countries: movie?.post_meta._amb_pais,
                  languages: movie?.post_meta._amb_idioma,
                }}
              ></Metadata>
            ) : null} */}
            {film_projections.cost !== null ? (
              <Text.Body className="text-neutral-500 " size="md">
                {film_projections.cost ? "Con costo" : "Gratuito"}
              </Text.Body>
            ) : null}
          </div>

          <div className="flex gap-3">
            {trailer ? (
              <Link href={trailer} target="_blank" className="z-20">
                <Button
                  size="sm"
                  label="Ver trailer"
                  color={"terciary-light"}
                ></Button>
              </Link>
            ) : null}

            {view_more ? (
              <Link href={view_more}>
                <Button
                  size="sm"
                  label="Ver detalle"
                  color={"terciary-light"}
                ></Button>
              </Link>
            ) : null}

            {venueUrl && (
              <Link href={venueUrl} target="_blank">
                <Button
                  size="sm"
                  label="Ubicar sede"
                  color={"terciary-dark"}
                  trailingIcon={<ExternalLinkIcon />}
                ></Button>
              </Link>
            )}

            {/* <Button
              size="sm"
              leadingIcon={<CalendarIcon />}
              label="Descargar"
              className="z-20 "
              color={"secondary-light"}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                createCalendarEvent(
                  {
                    hour: fromDate.getHours(),
                    day: fromDate.getDate(),
                    month: fromDate.getMonth(),
                    year: fromDate.getFullYear(),
                    diffMinutes: diffMinutes(fromDate, toDate),
                    title: movie_title,
                    location: "",
                    url: movie_link,
                  },
                  "digital"
                );
              }}
            ></Button> */}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

Projections.propTypes = {
  film_projections: PropTypes.object,
  theme: PropTypes.oneOf(["light", "dark"]),
};
